'use client';

import { useEffect, useMemo } from 'react';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { useProductActionErrors } from '@eliadmin/core/src/components/nt/product/use-product-action-errors';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductMinQuantity,
  getProductQuantityRules,
  getProductQuantityStep,
  isSellable,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';

/** ± with a 24 px hit area; the negative margin keeps the 20 px icon on the Figma grid. */
const STEP_BUTTON =
  '-mx-0.5 flex size-6 items-center justify-center rounded-[6px] text-[#0D110D] transition-colors hover:bg-[#F5F6F3] focus-visible:shadow-focus focus-visible:outline-none disabled:pointer-events-none disabled:text-[#A3ABA2]';

/** Core heart button in the Figma „Button Icon“ Outline L look (58 px, white with shadow). */
const FAVORITE_BUTTON =
  'size-[52px] shrink-0 rounded-2xl border-transparent px-0 shadow-float hover:border-[#CDD2CA] hover:shadow-none md:size-[58px]';
const FAVORITE_ICON =
  'size-6 text-[#0D110D] hover:text-[#0D110D] [[aria-pressed=true]_&]:fill-[#0D110D] [[aria-pressed=true]_&]:stroke-[#0D110D]';

/**
 * Figma „Actions“ (112:1289): quantity stepper 140 × 58, „Pridať do košíka“ Primary L and the
 * favourite button. Quantity rules, the „not in stock“ dialog, add to cart (variant dialog, analytics,
 * „Pridané do košíka“) and favourites are the core ones (`ProductQuantity`); failed requests show the
 * error toast like the product card.
 */
export function PurchaseActions({ variant }: { variant?: VariantsEntity }) {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product);
  const quantity = useVariantsStore(state => state.selectedQuantity);
  const setQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const settings = useGlobalStore(state => state.templateSettings.product_detail);
  const openDialog = useDialogStore(state => state.open);
  useProductActionErrors(product);

  const stock = variant ?? product;
  const rules = useMemo(() => getProductQuantityRules(product, variant), [product, variant]);
  const minQuantity = getProductMinQuantity(rules);
  const step = getProductQuantityStep(rules);
  const sellable = isSellable(stock, quantity);
  const available = stock.quantity > 0 || isSellable(stock, minQuantity);

  // Core `ProductQuantity` effects: a sellable item without stock starts at the minimum, every
  // quantity follows the step rules of the product / variant.
  useEffect(() => {
    if (stock.quantity <= 0 && stock.availability?.sellable) setQuantity(minQuantity);
  }, [stock, minQuantity, setQuantity]);
  useEffect(() => {
    const normalized = normalizeProductQuantity(quantity, rules);
    if (quantity > 0 && normalized !== quantity) setQuantity(normalized);
  }, [quantity, rules, setQuantity]);

  const changeTo = (value: number) => {
    const next = normalizeProductQuantity(value, rules);
    if (isSellable(stock, next)) {
      setQuantity(next);
    } else if (stock.quantity > 0) {
      openDialog({
        id: NotInStockDialog.id,
        content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit ?? ''} />,
      });
      setQuantity(normalizeProductQuantityDown(stock.quantity, rules));
    }
  };

  return (
    <div className="flex gap-2 sm:gap-3">
      <div className="flex h-[52px] w-[112px] shrink-0 items-center gap-2 rounded-2xl border border-[#E2E5DF] px-2 sm:w-[140px] sm:px-4 md:h-[58px]">
        <button
          type="button"
          aria-label={`${t('text22')} −`}
          disabled={!available || quantity <= minQuantity}
          onClick={() => changeTo(quantity - step)}
          className={STEP_BUTTON}
        >
          <Icon name="minus" size={20} />
        </button>
        <input
          type="text"
          inputMode="numeric"
          aria-label={t('text22')}
          disabled={!available}
          value={available ? quantity : minQuantity}
          onChange={event => {
            const value = parseInt(event.target.value, 10);
            if (value > 0) changeTo(value);
          }}
          onFocus={event => event.target.select()}
          className="nt-body-l-bold w-0 min-w-0 flex-1 bg-transparent text-center text-[#0D110D] focus-visible:outline-none disabled:text-[#A3ABA2]"
        />
        <button
          type="button"
          aria-label={`${t('text22')} +`}
          disabled={!available}
          onClick={() => changeTo(quantity + step)}
          className={STEP_BUTTON}
        >
          <Icon name="plus" size={20} />
        </button>
      </div>
      {/* The core button switches to its short label („Do košíka“) below 200 px of its own width. */}
      <div className="min-w-0 flex-1 @container">
        <AddToCartButton
          product={product}
          quantity={quantity}
          sellable={sellable}
          disabled={!sellable || quantity < 1}
          skipVariantDialog={settings?.skip_variant_dialog?.enabled}
          size="lg"
          iconLeft="shopping-cart-simple"
          className="w-full space-x-0 px-2 sm:px-7 [&>span]:flex-initial"
        />
      </div>
      {settings?.add_to_favorite?.enabled ? (
        <AddToFavoritesButton
          slug={product.slug}
          productId={product.id}
          isFavorite={product.is_favorite ?? false}
          buttonSize="icon"
          buttonClassName={FAVORITE_BUTTON}
          className={FAVORITE_ICON}
        />
      ) : null}
    </div>
  );
}
